import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  Box,
  Button,
  Grid,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DataListPage from "../../../components/DataListPage";
import DataListDateRange from "../../../components/DataListDateRange";
import TruncatedCell from "../../../components/TruncatedCell";
import user3Service from "../../../services/user3Service";
import { instanceToken } from "../../../utils/constant";
import { formatDetailDate } from "../../../utils/detailUtils";

export default function User3s() {
  const navigate = useNavigate();
  const [page, setPage] = useState(() => {
    const saved = sessionStorage.getItem("user3s_page");
    return saved ? parseInt(saved, 10) : 0;
  });
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const saved = sessionStorage.getItem("user3s_rowsPerPage");
    return saved ? parseInt(saved, 10) : 10;
  });
  const [count, setCount] = useState(0);
  const [users, setUsers] = useState(null);
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ from: "", to: "" });
  const [openCreate, setOpenCreate] = useState(false);
  const [createValues, setCreateValues] = useState({
    name: "",
    email: "",
    phone_number: "",
    customer_id: "",
    code: "",
    password: "",
    role: "USER",
  });

  const parse = useCallback((responseData) => {
    setUsers(responseData.cust_user);
    setCount(responseData.count);
  }, []);

  const fetchAllData = useCallback(async (searchValue, filterValues) => {
    try {
      const res = await user3Service.getAllUsersData(
        instanceToken.token,
        searchValue,
        filterValues
      );
      setData(res.data.cust_user);
    } catch (error) {
      console.error(error);
    }
  }, []);

  const fetchUsers = useCallback(
    async (limit, offset, searchValue, filterValues) => {
      try {
        const res = await user3Service.getAllUsers(
          instanceToken.token,
          limit,
          offset,
          searchValue,
          filterValues
        );
        parse(res.data);
      } catch (error) {
        console.error(error);
      }
    },
    [parse]
  );

  useEffect(() => {
    const offset = page * rowsPerPage;
    fetchUsers(rowsPerPage, offset, search, filters);
    fetchAllData(search, filters);
  }, [page, rowsPerPage, search, filters, fetchUsers, fetchAllData]);

  useEffect(() => {
    user3Service.searchUser(instanceToken.token, search).catch(console.error);
  }, [search]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    sessionStorage.setItem("user3s_page", newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = +event.target.value;
    setRowsPerPage(newRowsPerPage);
    sessionStorage.setItem("user3s_rowsPerPage", newRowsPerPage);
    setPage(0);
    sessionStorage.setItem("user3s_page", 0);
  };

  const handleOpenCreate = () => setOpenCreate(true);
  const handleCloseCreate = () => {
    setOpenCreate(false);
    setCreateValues({
      name: "",
      email: "",
      phone_number: "",
      customer_id: "",
      code: "",
      password: "",
      role: "USER",
    });
  };

  const handleCreate = async () => {
    const { name, email, phone_number, customer_id, password } = createValues;
    if (!name || !email || !phone_number || !customer_id || !password) {
      alert(
        "Please fill in all required fields: Name, Email, Phone, System ID (customer_id), and Password."
      );
      return;
    }

    try {
      await user3Service.createUser(instanceToken.token, createValues);
      handleCloseCreate();
      const offset = page * rowsPerPage;
      fetchUsers(rowsPerPage, offset, search, filters);
      fetchAllData(search, filters);
      alert("User created successfully!");
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.error || "Error creating user!");
    }
  };

  const exportRows = (rows) => {
    const dataToExport = rows.map((u) => ({
      SystemID: u.customer_id,
      CustomerID: u.code,
      User_Name: u.name,
      Phone: u.phone_number,
      Email: u.email,
      Role: u.role,
      Created_Date: u.created_at || u.createdAt,
      Otp_Exp_Time: u.otp_exp_time,
      One_Time_Password: u.one_time_password,
    }));

    const xls = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, xls, "User Level 3");
    XLSX.writeFile(wb, `${new Date().toISOString()}.xlsx`);
  };

  const openDetail = (row) => navigate(`/users3/${row.userid}`);

  const columns = [
    { id: "userid", label: "ID", render: (row) => row.userid },
    {
      id: "customer_id",
      label: "System ID",
      render: (row) => <TruncatedCell value={row.customer_id} />,
    },
    {
      id: "code",
      label: "Customer ID",
      render: (row) => <TruncatedCell value={row.code} />,
    },
    {
      id: "email",
      label: "Email",
      render: (row) => <TruncatedCell value={row.email} />,
    },
    {
      id: "name",
      label: "Name",
      render: (row) => <TruncatedCell value={row.name} />,
    },
    {
      id: "phone_number",
      label: "Phone Number",
      render: (row) => <TruncatedCell value={row.phone_number} />,
    },
    {
      id: "created_at",
      label: "Created Date",
      render: (row) =>
        formatDetailDate(row.created_at || row.createdAt) || "N/A",
    },
  ];

  return (
    <>
      <DataListPage
        title="User Level 3"
        rows={users || []}
        columns={columns}
        count={count}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        createButton={{ label: "Create", onClick: handleOpenCreate }}
        headerActions={[
          { label: "Export All Excel", onClick: () => exportRows(data) },
          { label: "Export to Excel", onClick: () => exportRows(users || []) },
        ]}
        search={{
          placeholder: "Search by name, email, phone, IDs...",
          onSearch: setSearch,
          live: true,
          showFilterButton: false,
        }}
        toolbarExtra={
          <DataListDateRange
            from={filters.from}
            to={filters.to}
            onChange={(next) => {
              setFilters(next);
              setPage(0);
            }}
            onClear={() => {
              setFilters({ from: "", to: "" });
              setSearch("");
              setPage(0);
            }}
          />
        }
        onView={openDetail}
        menuActions={[{ label: "View details", onClick: openDetail }]}
        getRowKey={(row) => row.userid}
        loading={!users}
      />

      <Modal open={openCreate} onClose={handleCloseCreate}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 500,
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
          }}
        >
          <Typography variant="h6" component="h2" mb={3} fontWeight={600}>
            Create New User (Level 3)
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name"
                value={createValues.name}
                onChange={(e) =>
                  setCreateValues({ ...createValues, name: e.target.value })
                }
                size="small"
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={createValues.email}
                onChange={(e) =>
                  setCreateValues({ ...createValues, email: e.target.value })
                }
                size="small"
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Phone Number"
                value={createValues.phone_number}
                onChange={(e) =>
                  setCreateValues({
                    ...createValues,
                    phone_number: e.target.value,
                  })
                }
                size="small"
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="System ID (customer_id)"
                value={createValues.customer_id}
                onChange={(e) =>
                  setCreateValues({
                    ...createValues,
                    customer_id: e.target.value,
                  })
                }
                size="small"
                required
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Customer ID (code)"
                value={createValues.code}
                onChange={(e) =>
                  setCreateValues({ ...createValues, code: e.target.value })
                }
                size="small"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                value={createValues.password}
                onChange={(e) =>
                  setCreateValues({
                    ...createValues,
                    password: e.target.value,
                  })
                }
                size="small"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Role"
                value={createValues.role}
                onChange={(e) =>
                  setCreateValues({ ...createValues, role: e.target.value })
                }
                size="small"
              />
            </Grid>
          </Grid>
          <Stack direction="row" spacing={2} justifyContent="flex-end" mt={4}>
            <Button onClick={handleCloseCreate} variant="outlined" color="inherit">
              Cancel
            </Button>
            <Button onClick={handleCreate} variant="contained" color="primary">
              Create
            </Button>
          </Stack>
        </Box>
      </Modal>
    </>
  );
}
